import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Sparkles,
  Calendar,
  MessageSquareWarning,
  Loader2,
  X,
  Save,
  FileText,
} from "lucide-react";
import { api, BRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MoneyInput from "@/components/MoneyInput";
import PhotoSlot from "@/components/PhotoSlot";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import ConsolidateDialog from "@/components/ConsolidateDialog";

const todayISO = () => new Date().toISOString().slice(0, 10);
const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + (iso.length === 10 ? "T12:00:00" : ""));
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
};

const statusBadge = (s) => {
  if (s === "contested") return { label: "Contestado", cls: "bg-amber-100 text-amber-800 border-amber-300" };
  if (s === "cancelled") return { label: "Cancelado", cls: "bg-slate-100 text-slate-500 border-slate-200 line-through" };
  if (s === "consolidated") return { label: "Consolidado", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  return { label: "Ativo", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" };
};

// Mini thumbnail que carrega a imagem via /files/{path}
function EntryThumb({ path, tag }) {
  const [src, setSrc] = React.useState(null);
  React.useEffect(() => {
    let obj;
    if (path) {
      api.get(`/files/${path}`, { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); })
        .catch(() => setSrc(null));
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!src) return null;
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="relative w-14 h-14 rounded-md overflow-hidden border border-slate-200 shrink-0 hover:ring-2 hover:ring-indigo-400"
    >
      <img src={src} alt={tag} className="w-full h-full object-cover" />
      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] text-center py-0.5">{tag}</span>
    </a>
  );
}

/**
 * Admin panel for FDP entries.
 * Shows timeline + add/edit modal + totals (consumption vs FDP limit)
 *
 * Props:
 *  - budget: { id, number, school_id }
 *  - fdpMax: number (school's FDP max_value)
 *  - onChange: fn (refetch budget)
 */
export default function FdpEntriesPanel({ budget, fdpMax = 0, onChange }) {
  const [entries, setEntries] = useState([]);
  const [totalActive, setTotalActive] = useState(0);
  const [totalContested, setTotalContested] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editEntry, setEditEntry] = useState(null); // null | {} = new | object = edit
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [consolidateOpen, setConsolidateOpen] = useState(false);

  const fetchEntries = useCallback(async () => {
    if (!budget?.id) return;
    setLoading(true);
    try {
      const r = await api.get("/fdp-entries", { params: { budget_id: budget.id } });
      setEntries(r.data?.items || []);
      setTotalActive(r.data?.total_active || 0);
      setTotalContested(r.data?.total_contested || 0);
    } catch (e) {
      toast.error("Falha ao carregar lançamentos");
    } finally {
      setLoading(false);
    }
  }, [budget?.id]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openNew = () => setEditEntry({
    description: "",
    value: 0,
    note: "",
    service_date: todayISO(),
    before_photo_path: null,
    after_photo_path: null,
  });

  const openEdit = (e) => setEditEntry({
    id: e.id,
    description: e.description || "",
    value: Number(e.value || 0),
    note: e.note || "",
    service_date: e.service_date || todayISO(),
    before_photo_path: e.before_photo_path || null,
    after_photo_path: e.after_photo_path || null,
  });

  const saveEntry = async () => {
    if (!editEntry) return;
    const desc = (editEntry.description || "").trim();
    const val = Number(editEntry.value || 0);
    if (!desc) { toast.error("Preencha a descrição"); return; }
    if (!val || val <= 0) { toast.error("Informe um valor"); return; }
    setSaving(true);
    try {
      const base = {
        description: desc,
        value: val,
        note: editEntry.note || "",
        service_date: editEntry.service_date || todayISO(),
        before_photo_path: editEntry.before_photo_path || null,
        after_photo_path: editEntry.after_photo_path || null,
      };
      if (editEntry.id) {
        await api.patch(`/fdp-entries/${editEntry.id}`, base);
        toast.success("Lançamento atualizado");
      } else {
        await api.post("/fdp-entries", { budget_id: budget.id, ...base });
        toast.success("Lançamento registrado — escola foi notificada");
      }
      setEditEntry(null);
      fetchEntries();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/fdp-entries/${toDelete.id}`);
      toast.success("Lançamento removido");
      setToDelete(null);
      fetchEntries();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover");
    }
  };

  const resolveContest = async (entry) => {
    try {
      await api.patch(`/fdp-entries/${entry.id}`, { status: "active" });
      toast.success("Contestação resolvida");
      fetchEntries();
    } catch {
      toast.error("Falha ao resolver");
    }
  };

  const percent = fdpMax > 0 ? Math.min(100, (totalActive / fdpMax) * 100) : 0;
  const nearLimit = percent >= 85;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-4 sm:p-5 space-y-4" data-testid="fdp-entries-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-heading text-base sm:text-lg font-black text-slate-900 leading-tight">Lançamentos FDP</h3>
            <p className="text-[11px] text-slate-500 leading-snug">Serviços registrados ao longo do período · escola notificada em tempo real</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto">
          {entries.length >= 2 && (
            <Button
              onClick={() => setConsolidateOpen(true)}
              variant="outline"
              className="border-violet-300 text-violet-700 hover:bg-violet-50 w-full sm:w-auto"
              data-testid="btn-consolidate"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              Consolidar com IA
            </Button>
          )}
          <Button
            onClick={openNew}
            className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto"
            data-testid="btn-new-fdp-entry"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Adicionar lançamento
          </Button>
        </div>
      </div>

      {/* Consumption meter */}
      <div className="bg-gradient-to-br from-indigo-50 to-sky-50 border border-indigo-200 rounded-xl p-3 sm:p-4">
        <div className="flex items-end justify-between gap-2 flex-wrap">
          <div>
            <p className="text-[10px] text-indigo-700 font-bold uppercase tracking-wider">Consumido no período</p>
            <p className="text-xl sm:text-2xl font-black text-indigo-900 tabular-nums mt-0.5">{BRL(totalActive)}</p>
            {fdpMax > 0 && (
              <p className="text-xs text-slate-600 mt-0.5">de {BRL(fdpMax)} ({percent.toFixed(0)}%)</p>
            )}
          </div>
          {totalContested > 0 && (
            <div className="text-right">
              <p className="text-[10px] text-amber-700 font-bold uppercase tracking-wider flex items-center gap-1 justify-end">
                <MessageSquareWarning className="w-3 h-3" /> Em contestação
              </p>
              <p className="text-base font-black text-amber-700 tabular-nums">{BRL(totalContested)}</p>
            </div>
          )}
        </div>
        {fdpMax > 0 && (
          <div className="mt-2 w-full h-2 bg-white rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${percent}%`,
                background: percent >= 100 ? "#dc2626" : nearLimit ? "#f59e0b" : "#4f46e5",
              }}
            />
          </div>
        )}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando...
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-10 text-slate-500 border-2 border-dashed border-slate-200 rounded-xl">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm font-medium">Nenhum lançamento ainda</p>
          <p className="text-xs">Clique em "Adicionar lançamento" para registrar o primeiro serviço prestado.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="fdp-entries-list">
          {entries.map((e) => {
            const b = statusBadge(e.status);
            return (
              <div
                key={e.id}
                className={`border rounded-xl p-3 transition-colors ${
                  e.status === "contested" ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"
                }`}
                data-testid={`fdp-entry-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">{e.description}</p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-0.5">
                        <Calendar className="w-3 h-3" /> {formatDate(e.service_date)}
                      </span>
                      <span className={`inline-flex items-center gap-0.5 border rounded-full px-1.5 py-0.5 font-semibold ${b.cls}`}>
                        {b.label}
                      </span>
                      {e.note && <span className="italic">· {e.note}</span>}
                    </div>
                    {e.status === "contested" && e.contest_reason && (
                      <div className="mt-2 bg-white border border-amber-200 rounded-lg p-2 text-[11px] text-amber-900">
                        <p className="font-bold flex items-center gap-1">
                          <MessageSquareWarning className="w-3 h-3" /> Motivo da contestação:
                        </p>
                        <p className="mt-0.5">{e.contest_reason}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolveContest(e)}
                          className="h-6 text-[10px] mt-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        >
                          Marcar como resolvido
                        </Button>
                      </div>
                    )}
                    {(e.before_photo_path || e.after_photo_path) && (
                      <div className="flex gap-2 mt-2" data-testid={`entry-photos-${e.id}`}>
                        {e.before_photo_path && (
                          <EntryThumb path={e.before_photo_path} tag="Antes" />
                        )}
                        {e.after_photo_path && (
                          <EntryThumb path={e.after_photo_path} tag="Depois" />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right flex flex-col items-end gap-1">
                    <p className="text-base sm:text-lg font-black text-indigo-700 tabular-nums whitespace-nowrap">
                      {BRL(e.value)}
                    </p>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={() => openEdit(e)}
                        className="text-slate-300 hover:text-indigo-600 p-1"
                        aria-label="Editar"
                        data-testid={`btn-edit-entry-${e.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setToDelete(e)}
                        className="text-slate-300 hover:text-rose-600 p-1"
                        aria-label="Remover"
                        data-testid={`btn-delete-entry-${e.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit dialog */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent data-testid="fdp-entry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              {editEntry?.id ? "Editar lançamento" : "Novo lançamento FDP"}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              {editEntry?.id ? "A escola será notificada se o valor mudar." : "Ao salvar, a escola recebe uma notificação e vê o lançamento imediatamente."}
            </p>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Descrição do serviço *</Label>
                <Textarea
                  value={editEntry.description}
                  onChange={(e) => setEditEntry((v) => ({ ...v, description: e.target.value }))}
                  rows={2}
                  placeholder="Ex.: Desentupimento de vaso sanitário - banheiro feminino"
                  data-testid="input-entry-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-emerald-700">Valor cobrado (R$) *</Label>
                  <MoneyInput
                    value={editEntry.value}
                    onChange={(v) => setEditEntry((e) => ({ ...e, value: v }))}
                    data-testid="input-entry-value"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">Data do serviço</Label>
                  <Input
                    type="date"
                    value={editEntry.service_date || todayISO()}
                    onChange={(e) => setEditEntry((v) => ({ ...v, service_date: e.target.value }))}
                    data-testid="input-entry-date"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Observação (opcional)</Label>
                <Textarea
                  value={editEntry.note || ""}
                  onChange={(e) => setEditEntry((v) => ({ ...v, note: e.target.value }))}
                  rows={2}
                  placeholder="Ex.: inclui material, altura 3m, serviço emergencial..."
                  data-testid="input-entry-note"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <PhotoSlot
                  label="Antes"
                  path={editEntry.before_photo_path}
                  onPathChange={(p) => setEditEntry((v) => ({ ...v, before_photo_path: p }))}
                  testid="fdp-photo-before"
                />
                <PhotoSlot
                  label="Depois"
                  path={editEntry.after_photo_path}
                  onPathChange={(p) => setEditEntry((v) => ({ ...v, after_photo_path: p }))}
                  testid="fdp-photo-after"
                />
              </div>
              <p className="text-[10px] text-slate-500 -mt-1">
                As fotos ficam disponíveis para a escola conferir o serviço prestado.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancelar</Button>
            <Button
              onClick={saveEntry}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-save-entry"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Salvando...</>
              ) : (
                <><Save className="w-4 h-4 mr-1.5" /> Salvar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent data-testid="fdp-entry-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertCircle className="w-5 h-5" />
              Remover lançamento?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Esta ação remove o lançamento <b>"{toDelete?.description}"</b> ({BRL(toDelete?.value || 0)}) e devolve o valor ao saldo FDP da escola.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Cancelar</Button>
            <Button
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="btn-confirm-delete-entry"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consolidation with AI */}
      <ConsolidateDialog
        open={consolidateOpen}
        onOpenChange={setConsolidateOpen}
        budget={budget}
        onDone={() => { fetchEntries(); onChange?.(); }}
      />
    </div>
  );
}
